import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { geoPath, geoMercator } from 'd3-geo';
import { scalePow } from 'd3-scale';
import { interpolateBlues } from 'd3-scale-chromatic';
import worldGeoJson from '../../data/world.geo.json';
import { normalizeCountry, getCountryName } from '../../utils/countryMapping';

const CountryDistributionMap = ({ data }) => {
  const navigate = useNavigate();

  // Transform data to use ISO codes with memoization
  const isoData = useMemo(() => {
    const unmappedCountries = new Set();
    const result = Object.entries(data).reduce((acc, [country, count]) => {
      const isoCode = normalizeCountry(country);
      if (isoCode) {
        // Aggregate counts for the same country
        acc[isoCode] = (acc[isoCode] || 0) + count;
      } else {
        unmappedCountries.add(country);
      }
      return acc;
    }, {});

    // Log any unmapped countries for debugging
    if (unmappedCountries.size > 0) {
      console.warn('Unmapped countries:', Array.from(unmappedCountries));
    }

    return result;
  }, [data]);

  // Create projection and path generator
  const projection = geoMercator()
    .scale(200)
    .center([0, 30])
    .translate([450, 350]); // Adjusted for container

  const pathGenerator = geoPath().projection(projection);

  // Calculate total coins and max count
  const totalCoins = Object.values(isoData).reduce((sum, count) => sum + count, 0);
  const maxCount = Math.max(...Object.values(isoData), 1);
  const colorScale = scalePow()
    .exponent(0.3) // Use an exponent < 1 to give more visual weight to smaller values
    .domain([0, maxCount])
    .range([0, 1])
    .clamp(true);

  const colorMap = (value) => interpolateBlues(colorScale(value));

  // Create legend items with more meaningful intervals
  const legendItems = [1, 5, 10, 50, 100, maxCount]
    .filter(value => value <= maxCount)
    .map(value => ({
      color: colorMap(value),
      label: value === maxCount ? `${value}+` : value
    }));

  // State for tooltip
  const [tooltip, setTooltip] = useState({ show: false, content: '', x: 0, y: 0 });

  return (
    <div className="h-[800px] w-full flex flex-col overflow-hidden relative">
      {/* Tooltip */}
      {tooltip.show && (
        <div
          className="fixed z-10 bg-black bg-opacity-75 text-white px-3 py-1.5 rounded text-sm pointer-events-none"
          style={{
            left: tooltip.x,
            top: tooltip.y,
            transform: 'translate(10px, 10px)'
          }}
        >
          {tooltip.content}
        </div>
      )}
      <div className="flex-1 relative overflow-hidden">
        <svg
          viewBox="0 0 900 860"
          className="w-full h-full"
          style={{ backgroundColor: '#f8fafc' }}
        >
          {/* Map */}
          {worldGeoJson.features.map((feature) => {
            // Use ISO_A3_EH if ISO_A3 is -99, otherwise use ISO_A3
            const countryCode = feature.properties.ISO_A3 === "-99" 
              ? feature.properties.ISO_A3_EH 
              : feature.properties.ISO_A3;
            const count = isoData[countryCode] || 0;

            return (
              <path
                key={feature.properties.NAME}
                d={pathGenerator(feature)}
                fill={count > 0 ? colorMap(count) : '#e2e8f0'}
                stroke="#cbd5e1"
                strokeWidth={0.5}
                className={`transition-colors duration-200 hover:brightness-90 ${count > 0 ? 'cursor-pointer' : ''}`}
                onMouseMove={(e) => {
                  const countryName = getCountryName(countryCode, worldGeoJson.features);
                  const percentage = ((count / totalCoins) * 100).toFixed(1);
                  setTooltip({
                    show: true,
                    content: `${countryName}: ${count} ${count === 1 ? 'coin' : 'coins'} (${percentage}%)`,
                    x: e.clientX,
                    y: e.clientY
                  });
                }}
                onMouseLeave={() => setTooltip({ show: false, content: '', x: 0, y: 0 })}
                onClick={() => {
                  if (count > 0) {
                    // Find all country names that map to this ISO code
                    const matchingCountries = Object.entries(data)
                      .filter(([country]) => normalizeCountry(country) === countryCode)
                      .map(([country]) => country);

                    if (matchingCountries.length > 0) {
                      const params = new URLSearchParams(window.location.search);
                      // Construct a filter expression with OR conditions
                      const filterExpr = matchingCountries.length === 1
                        ? matchingCountries[0]
                        : `(${matchingCountries.join(' OR ')})`;
                      params.set('country', filterExpr);
                      navigate(`/inventory?${params.toString()}`);
                    }
                  }
                }}
              />
            );
          })}

          {/* Legend */}
          <g transform="translate(-250, 730)">
            <text x="0" y="0" className="text-sm fill-gray-600 font-medium">Coins:</text>
            <g transform="translate(0, 20)">
              {legendItems.map((item, index) => (
                <g key={index} transform={`translate(${index * 50}, 0)`}>
                  <rect
                    width="35"
                    height="15"
                    fill={item.color}
                    stroke="#cbd5e1"
                    strokeWidth="0.5"
                  />
                  <text
                    x="17.5"
                    y="30"
                    textAnchor="middle"
                    className="text-xs fill-gray-600"
                  >
                    {item.label}
                  </text>
                </g>
              ))}
            </g>
          </g>
        </svg>
      </div>
    </div>
  );
};

export default CountryDistributionMap;
